import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export type TriggerType =
  | 'deal_stage_changed'
  | 'meeting_outcome_set'
  | 'task_completed'
  | 'enrichment_confidence_below'
  | 'missing_enrichment_data';
export type StepType = 'delay' | 'create_task' | 'add_note' | 'notify' | 'email_send';

export interface Workflow {
  id: string;
  organization_id: string;
  name: string;
  is_active: boolean;
  trigger_type: TriggerType;
  trigger_config?: Record<string, any> | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface WorkflowCreateInput {
  organization_id: string;
  name: string;
  is_active?: boolean;
  trigger_type: TriggerType;
  trigger_config?: Record<string, any>;
}

export interface WorkflowUpdateInput {
  id: string;
  organization_id?: string;
  name?: string;
  is_active?: boolean;
  trigger_type?: TriggerType;
  trigger_config?: Record<string, any>;
}

export interface WorkflowStep {
  id: string;
  workflow_id: string;
  step_order: number;
  step_type: StepType;
  config: Record<string, any>;
}

export interface WorkflowStepCreateInput {
  workflow_id: string;
  step_order: number;
  step_type: StepType;
  config?: Record<string, any>;
}

export interface WorkflowStepUpdateInput {
  id: string;
  workflow_id: string;
  step_order?: number;
  step_type?: StepType;
  config?: Record<string, any>;
}

export interface WorkflowRun {
  id: string;
  workflow_id: string | null;
  organization_id: string;
  trigger_type: TriggerType;
  entity_type: string;
  entity_id: string;
  status: 'running' | 'completed' | 'failed';
  started_at: string;
  finished_at?: string;
}

export interface WorkflowLog {
  id: string;
  run_id: string;
  step_id?: string | null;
  level: 'info' | 'warn' | 'error';
  message: string;
  meta?: Record<string, any> | null;
  created_at: string;
}

const workflowsService = {
  async list(organization_id: string, limit = 50, offset = 0): Promise<{ data: Workflow[]; count?: number }>{
    validateRequiredParams({ organization_id }, ['organization_id'], {
      service: 'workflowsService.list'
    });
    const url = buildApiUrl('/api/crm/workflows', { organization_id, limit, offset });
    return fetchJSON(url, {
      description: 'List workflows',
      context: { organization_id, limit, offset }
    });
  },
  async create(payload: WorkflowCreateInput): Promise<{ data: Workflow }>{
    validateRequiredParams(payload, ['organization_id', 'name', 'trigger_type'], {
      service: 'workflowsService.create'
    });
    const url = buildApiUrl('/api/crm/workflows');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create workflow',
      context: { organization_id: payload.organization_id }
    });
  },
  async update(id: string, updates: WorkflowUpdateInput): Promise<{ data: Workflow }>{
    validateRequiredParams({ id }, ['id'], {
      service: 'workflowsService.update'
    });
    const url = buildApiUrl(`/api/crm/workflows/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: updates,
      description: 'Update workflow',
      context: { id, organization_id: updates.organization_id }
    });
  },
  async remove(id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id }, ['id'], {
      service: 'workflowsService.remove'
    });
    const url = buildApiUrl(`/api/crm/workflows/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete workflow',
      context: { id }
    });
  },
  async listSteps(workflow_id: string): Promise<{ data: WorkflowStep[] }>{
    validateRequiredParams({ workflow_id }, ['workflow_id'], {
      service: 'workflowsService.listSteps'
    });
    const url = buildApiUrl(`/api/crm/workflows/${encodeURIComponent(workflow_id)}/steps`);
    return fetchJSON(url, {
      description: 'List workflow steps',
      context: { workflow_id }
    });
  },
  async createStep(workflow_id: string, payload: Omit<WorkflowStepCreateInput, 'workflow_id'>): Promise<{ data: WorkflowStep }>{
    validateRequiredParams({ workflow_id, ...payload }, ['workflow_id', 'step_order', 'step_type'], {
      service: 'workflowsService.createStep'
    });
    const url = buildApiUrl(`/api/crm/workflows/${encodeURIComponent(workflow_id)}/steps`);
    return fetchJSON(url, {
      method: 'POST',
      body: { ...payload },
      description: 'Create workflow step',
      context: { workflow_id, step_order: payload.step_order, step_type: payload.step_type }
    });
  },
  async updateStep(workflow_id: string, id: string, updates: Omit<WorkflowStepUpdateInput, 'workflow_id' | 'id'>): Promise<{ data: WorkflowStep }>{
    validateRequiredParams({ workflow_id, id }, ['workflow_id', 'id'], {
      service: 'workflowsService.updateStep'
    });
    const url = buildApiUrl(`/api/crm/workflows/${encodeURIComponent(workflow_id)}/steps/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: updates,
      description: 'Update workflow step',
      context: { workflow_id, id }
    });
  },
  async removeStep(workflow_id: string, id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ workflow_id, id }, ['workflow_id', 'id'], {
      service: 'workflowsService.removeStep'
    });
    const url = buildApiUrl(`/api/crm/workflows/${encodeURIComponent(workflow_id)}/steps/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete workflow step',
      context: { workflow_id, id }
    });
  },
  async listRuns(workflow_id: string, organization_id: string): Promise<{ data: WorkflowRun[] }>{
    validateRequiredParams({ workflow_id, organization_id }, ['workflow_id', 'organization_id'], {
      service: 'workflowsService.listRuns'
    });
    const url = buildApiUrl(`/api/crm/workflows/${encodeURIComponent(workflow_id)}/runs`, { organization_id });
    return fetchJSON(url, {
      description: 'List workflow runs',
      context: { workflow_id, organization_id }
    });
  },
  async listLogs(run_id: string): Promise<{ data: WorkflowLog[] }>{
    validateRequiredParams({ run_id }, ['run_id'], {
      service: 'workflowsService.listLogs'
    });
    const url = buildApiUrl(`/api/crm/workflow-runs/${encodeURIComponent(run_id)}/logs`);
    return fetchJSON(url, {
      description: 'List workflow logs',
      context: { run_id }
    });
  }
};

export default workflowsService;
